import { localApiBase } from "../../lib/localApi";

export interface AgentRoomResponse {
  roomName: string;

  token: string;

  livekitUrl: string;
}

export interface AgentSettings {
  livekit: {
    host: string;

    port: number;

    apiKey: string;

    apiSecret: string;
  };

  openaiApiKey: string;
  llmBaseUrl: string;
  llmModel: string;
  defaults: {
    systemPrompt: string;

    language: string;

    whisperModel: string;

    piperVoice: string;
  };
}

export interface AgentStatus {
  livekitServer: boolean;

  activeRooms: Array<{
    roomName: string;

    createdAt: number;

    config: Record<string, string>;
  }>;

  runningAgents: string[];
}

export interface OllamaModel {
  name: string;

  size: number;

  parameterSize: string;
  capabilities?: string[];
}

export interface OllamaStatus {
  available: boolean;

  models: OllamaModel[];
}

class AgentService {
  private get Domain() {
    return localApiBase();
  }

  private async request(
    path: string,
    options: RequestInit = {},
  ): Promise<Response> {
    let response: Response;
    try {
      response = await fetch(`${this.Domain}/api/agent${path}`, {
        signal: AbortSignal.timeout(15000),
        ...options,
      });
    } catch (error) {
      if (options.signal?.aborted) throw error;
      throw new Error(
        "The desktop agent service is unavailable. Check Eriol Desktop and retry.",
      );
    }
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(
        payload?.message ||
          payload?.error ||
          `Agent request failed (${response.status})`,
      );
    }
    return response;
  }

  async getSettings(): Promise<AgentSettings> {
    const response = await this.request("/settings");
    const payload = await response.json();

    return payload.data;
  }

  async updateSettings(
    settings: Partial<AgentSettings>,
  ): Promise<AgentSettings> {
    const response = await this.request("/settings", {
      method: "POST",

      headers: { "Content-Type": "application/json" },

      body: JSON.stringify(settings),
    });

    const payload = await response.json();

    return payload.data;
  }

  async createRoom(config?: {
    agentName?: string;

    systemPrompt?: string;

    language?: string;

    whisperModel?: string;

    piperVoice?: string;
  }): Promise<AgentRoomResponse> {
    const response = await this.request("/room", {
      method: "POST",

      headers: { "Content-Type": "application/json" },

      body: JSON.stringify(config ?? {}),
    });

    const payload = await response.json();

    return payload.data;
  }

  async deleteRoom(roomName: string): Promise<void> {
    await this.request(`/room/${encodeURIComponent(roomName)}`, {
      method: "DELETE",
    });
  }

  async getStatus(): Promise<AgentStatus> {
    const response = await this.request("/status");
    const payload = await response.json();

    return payload.data;
  }

  async getOllamaModels(signal?: AbortSignal): Promise<OllamaStatus> {
    const response = await this.request(
      "/ollama-models",
      signal
        ? { signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]) }
        : {},
    );
    const payload: OllamaStatus = await response.json();
    return {
      ...payload,
      models: payload.models.filter(
        (model) =>
          !model.capabilities || model.capabilities.includes("completion"),
      ),
    };
  }

  async getSetupStatus(): Promise<{ ready: boolean }> {
    const response = await this.request("/setup-status");
    const payload = await response.json();

    return payload.data;
  }

  /**
   * SSE endpoint that streams install progress.
   * Calls onProgress with { stage, message } for each event.
   * Returns when the stream ends.
   */

  async setup(
    onProgress: (stage: string, message: string) => void,
  ): Promise<boolean> {
    const response = await this.request("/setup", {
      method: "POST",
      signal: AbortSignal.timeout(15 * 60 * 1000),
    });

    if (!response.ok || !response.body) {
      throw new Error("Failed to start agent setup");
    }

    const reader = response.body.getReader();

    const decoder = new TextDecoder();

    let buffer = "";

    let success = false;

    const processLine = (line: string) => {
      if (!line.startsWith("data:")) return;
      const event = JSON.parse(line.slice(5).trim());
      onProgress(event.stage, event.message);
      if (event.stage === "ready") success = true;
      if (event.stage === "error") throw new Error(event.message);
    };
    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          buffer += decoder.decode();
          if (buffer.trim()) processLine(buffer.trim());
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");

        buffer = lines.pop() ?? "";

        for (const line of lines) {
          processLine(line);
        }
      }
      if (!success)
        throw new Error(
          "Agent setup ended before the environment was ready. Retry setup.",
        );
      return true;
    } finally {
      await reader.cancel().catch(() => {});
      reader.releaseLock();
    }
  }

  async startOpenRouterAuth(): Promise<void> {
    await this.request("/openrouter/auth", {
      method: "POST",
    });
  }

  async pollOpenRouterAuth(): Promise<{
    status: string;
    apiKey?: string;
    error?: string;
  }> {
    const response = await this.request("/openrouter/status");
    return await response.json();
  }
}

export default AgentService;
